import { all, fork, put, takeEvery, call } from "redux-saga/effects";
import { SagaIterator } from "@redux-saga/core";

// actions
import updateAction, { updateRequest } from "./uiActions";

// constants
import { UIActionTypes } from "./constants";
import { get as getRequest } from "../../helpers/api/request";
import { chatroomDetail, awaitingOfferPackages } from "../../helpers/api/chat";
import { toast } from "react-toastify";

const TRANSPORTATION_OPTIONS = [
  { value: 1, label: "Private Jet" },
  { value: 2, label: "Flight Ticket" },
  { value: 6, label: "Yacht Rental" },
  { value: 9, label: "Car Rental" },
  { value: 10, label: "Driver Car Rental" },
];

const TRANSPORT_SERVICE_IDS = [1, 2, 6, 9, 10];

const normalizeLocationOption = (location: any) => {
  if (!location) {
    return "";
  }

  if (location.value) {
    return location;
  }

  return {
    ...location,
    label: location.label || location.name || "",
    value: `${location.id}${location.type ? `-${location.type}` : ""}`,
  };
};

const normalizeTransportParameters = (service: any) => {
  if (!service?.parameters) {
    return service;
  }

  const parameters = { ...service.parameters };

  if (!parameters.transportationType || !parameters.transportationType?.value) {
    parameters.transportationType =
      TRANSPORTATION_OPTIONS.find((option) => option.value === service.serviceId) ||
      parameters.transportationType ||
      "";
  }

  if (parameters.tripType) {
    parameters.tripType =
      typeof parameters.tripType === "object" && parameters.tripType?.value
        ? parameters.tripType
        : {
            value: parameters.tripType,
            label: parameters.tripType,
          };
  } else {
    parameters.tripType = "";
  }

  parameters.departureFrom = normalizeLocationOption(parameters.departureFrom);
  parameters.departureTo = normalizeLocationOption(parameters.departureTo);
  parameters.returnFrom = normalizeLocationOption(parameters.returnFrom);
  parameters.returnTo = normalizeLocationOption(parameters.returnTo);

  return {
    ...service,
    parameters,
  };
};

const normalizeService = (service: any, index: number) => {
  if (!service) {
    return service;
  }

  let normalized = { ...service };

  if (TRANSPORT_SERVICE_IDS.includes(normalized.serviceId)) {
    normalized = normalizeTransportParameters(normalized);
  }

  if (!normalized.refKey) {
    const baseKey = normalized.id ?? `${normalized.serviceId}-${index}`;
    normalized.refKey = `service-${baseKey}`;
  }

  return normalized;
};

const normalizeRequestPayload = (payload: any) => {
  if (!payload) {
    return payload;
  }

  const services = Array.isArray(payload.services)
    ? payload.services.map((service: any, index: number) => normalizeService(service, index))
    : [];

  return {
    ...payload,
    services,
  };
};

function* getChatroomSaga(data: any): SagaIterator {
  try {
    yield put(updateAction({ loading: true }));
    const chatroomId = data.payload.chatroomId;
    // @ts-ignore
    const response = yield call(chatroomDetail, chatroomId);

    yield put(updateAction({ selectedRoom: response?.data?.data }));
    yield put(updateAction({ customer: response?.data?.data?.customer }));

  } catch (e: any) {
    if (!e.message) {
      toast.error(e);
    }
  } finally {
    yield put(updateAction({ loading: false }));
  }
}

function* getRequestSaga(data: any): SagaIterator {
  try {
    yield put(updateAction({ loading: true }));
    const requestId = data.payload.requestId;
    // @ts-ignore
    const response = yield call(getRequest, requestId);

  const normalizedPayload = normalizeRequestPayload(response.data?.data);
  yield put(updateRequest(normalizedPayload));
  } catch (e: any) {
    console.log(e);
    if (!e.message) {
      toast.error(e);
      data.navigate("/dashboard");
    }
  } finally {
    yield put(updateAction({ loading: false }));
  }
}

function* getAwaitingSupervisorList(data: any): SagaIterator {
  try {
    yield put(updateAction({ loading: true }));
    // @ts-ignore
    const response = yield call(awaitingOfferPackages);
    yield put(updateAction({ awaitingSupervisorList: response?.data?.data }));
  } catch (e: any) {
    console.log(e);
    if (!e.message) {
      toast.error(e);
    }
  } finally {
    yield put(updateAction({ loading: false }));
  }
}

export function* watchGetRequest() {
  yield takeEvery(UIActionTypes.REQUEST, getRequestSaga);
  yield takeEvery(UIActionTypes.CHATROOM, getChatroomSaga);
  yield takeEvery(
    UIActionTypes.AWAITING_SUPERVISOR_OFFER_PACKAGES,
    getAwaitingSupervisorList
  );
}

function* uiSaga() {
  yield all([fork(watchGetRequest)]);
}

export default uiSaga;
